<template>
    <div v-if="featureFlags['live-site-responsive-design']" :class="{ 'g-p-6': isPage }">
        <g-button v-if="isPage" icon="angle-left" class="g-mb-6" size="small" @click="handleBack"> Back </g-button>

        <div v-if="!useAuthStore().isLoggedIn">
            <div class="margin-bottom-normal">
                <account-verification-warning @verify-account="$root.go('v2.account.verify')" />
            </div>

            <base-box background-color="grey">
                <template #content>
                    <g-typography>
                        In order to change your settings, we need to verify that you are the owner of this account.
                    </g-typography>
                </template>
            </base-box>
        </div>

        <div v-else>
            <g-loader v-if="useCreatorStore().loadingLiveUrls" />
            <div v-else-if="displayMode === SETTINGS_DISPLAY_MODE_BRANDS">
                <settings-brands :is-page="isPage" />
            </div>
            <div v-else>
                <g-typography v-if="isPage" class="g-mb-6" tag="h1" font-weight="semibold" value="Settings" />

                <!-- Payments -->
                <div v-if="$root.account_makes_payments && !$root.contact.last_verified_email">
                    <email-verification />
                </div>
                <g-divider v-if="$root.account_makes_payments && !$root.contact.last_verified_email" margin="large" />

                <!-- Live Site Switcher -->
                <div v-if="showLiveSiteSwitcher">
                    <live-site-switcher @show-brands="handleShowBrands">
                        <g-typography tag="h3" font-weight="semibold">Brand Live Site</g-typography>
                        <g-typography class="g-mt-3 g-mb-5" tag="p" color="grey-6">
                            Switch between the Live URLs of brands you’re actively working with.
                        </g-typography>
                    </live-site-switcher>
                </div>
                <g-divider margin="large" />

                <!-- Address -->
                <div>
                    <address-info>
                        <g-typography tag="h3" font-weight="semibold">Shipping Info</g-typography>
                        <g-typography class="g-mt-3 g-mb-5" tag="p" color="grey-6">
                            Make sure your address is up to date so you can receive product for your sponsorship
                            campaigns.
                        </g-typography>
                    </address-info>
                </div>
                <g-divider margin="large" />

                <!-- Contact Info -->
                <div>
                    <contact-info>
                        <g-typography tag="h3" font-weight="semibold">Contact Information</g-typography>
                        <g-typography class="g-mt-3 g-mb-5" tag="p" color="grey-6">
                            Keep your information up to date so you can receive emails regarding new sponsorships.
                        </g-typography>
                    </contact-info>
                </div>
                <g-divider margin="large" />

                <!-- Agent Info -->
                <div v-if="showAgentInfo">
                    <agent-info>
                        <g-typography tag="h3" font-weight="semibold">Talent Agent Information and Settings</g-typography>
                        <g-typography class="g-mt-3 g-mb-5" tag="p" color="grey-6">
                            Add talent agent contact information to update payment settings.
                        </g-typography>
                    </agent-info>
                    <g-divider margin="large" />
                </div>

                <!-- Notification Settings -->
                <div>
                    <notification-settings v-if="showNotificationSettings">
                        <g-typography tag="h3" font-weight="semibold">Notifications</g-typography>
                        <g-typography class="g-mt-3 g-mb-5" tag="p" color="grey-6">
                            We will send you notifications when important things happen!
                        </g-typography>
                    </notification-settings>
                    <notification-channels v-else>
                        <g-typography tag="h3" font-weight="semibold">Notification Settings</g-typography>
                        <g-typography class="g-mt-3 g-mb-5" tag="p" color="grey-6">
                            We will send you notifications when important things happen! Choose how you would like to be
                            notified.
                        </g-typography>
                    </notification-channels>
                </div>
                <g-divider margin="large" />

                <div>
                    <request-removal />
                </div>
            </div>
        </div>
    </div>

    <div v-else>
        <div v-if="!useAuthStore().isLoggedIn" class="box p-25">
            <div class="margin-bottom-normal">
                <account-verification-warning @verify-account="$root.go('v2.account.verify')" />
            </div>

            <base-box background-color="grey">
                <template #content>
                    <g-typography>
                        In order to change your settings, we need to verify that you are the owner of this account.
                    </g-typography>
                </template>
            </base-box>
        </div>
        <div v-else-if="displayMode === SETTINGS_DISPLAY_MODE_BRANDS">
            <div class="box">
                <settings-brands is-page>
                    <g-button icon="angle-left" class="g-mb-6" size="small" @click="handleBack"> Back </g-button>
                </settings-brands>
            </div>
        </div>
        <div v-else>
            <g-loader v-if="useCreatorStore().loadingLiveUrls" />
            <div v-else>
                <div class="box">
                    <a class="mr-10" @click.prevent="showSection('addressRef')">Address</a>
                    <a class="mr-10" @click.prevent="showSection('contactRef')">Contact Info</a>
                    <a class="mr-10" @click.prevent="showSection('notificationsRef')">Notifications</a>
                </div>

                <!-- Payments -->
                <div v-if="$root.account_makes_payments && !$root.contact.last_verified_email" class="box p-25">
                    <email-verification />
                </div>

                <!-- Live Site Switcher -->
                <div v-if="showLiveSiteSwitcher" class="box">
                    <live-site-switcher @show-brands="handleShowBrands">
                        <g-typography tag="h4" font-weight="semibold">Brand Live Site</g-typography>
                        <g-typography class="g-mt-3 g-mb-5" tag="p" color="grey-6">
                            Switch between the Live URLs of brands you’re actively working with.
                        </g-typography>
                    </live-site-switcher>
                </div>
                <g-divider margin="large" />

                <!-- Address -->
                <div ref="addressRef" class="box p-25">
                    <address-info>
                        <div class="notification">
                            <strong>Shipping Info</strong><br />
                            Make sure your address is up to date so you can receive product for your sponsorship
                            campaigns.
                        </div>
                    </address-info>
                </div>

                <!-- Contact Info -->
                <div ref="contactRef" class="box p-25">
                    <contact-info>
                        <div class="notification">
                            <strong>Contact Information</strong><br />
                            Keep your information up to date so you can receive emails regarding new sponsorships.
                        </div>
                    </contact-info>
                </div>

                <!-- Agent Info -->
                <div v-if="showAgentInfo" ref="agentRef" class="box p-25">
                    <agent-info>
                        <div class="notification">
                            <strong>Talent Agent Information and Settings</strong><br />
                            Add talent agent contact information to update payment settings.
                        </div>
                    </agent-info>
                </div>

                <!-- Notification Settings -->
                <div ref="notificationsRef" class="box p-25">
                    <notification-settings v-if="featureFlags['creator-reminders']">
                        <div class="notification">
                            <strong>Notification Settings</strong><br />
                            We will send you notifications when important things happen! Choose how you would like to be
                            notified.
                        </div>
                    </notification-settings>
                    <notification-channels v-else>
                        <div class="notification">
                            <strong>Notifications</strong><br />
                            We will send you notifications when important things happen!
                        </div>
                    </notification-channels>
                </div>

                <div class="box p-25">
                    <request-removal />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { ref, toRefs, onMounted, watch, computed } from 'vue-demi';
    import { SETTINGS_DISPLAY_MODE_DEFAULT, SETTINGS_DISPLAY_MODE_BRANDS } from '../../../../app-v2/constants/settings';
    import ContactInfo from './profile/ContactInfo.vue';
    import AddressInfo from './profile/AddressInfo.vue';
    import AgentInfo from './profile/AgentInfo.vue';
    import EmailVerification from './profile/EmailVerification.vue';
    import NotificationChannels from './profile/NotificationChannels.vue';
    import NotificationSettings from './profile/NotificationSettings.vue';
    import RequestRemoval from './profile/RequestRemoval.vue';
    import LiveSiteSwitcher from './profile/LiveSiteSwitcher.vue';
    import SettingsBrands from './profile/SettingsBrands.vue';
    import AccountVerificationWarning from '../../../../app-v2/components/data-views/dashboard/partials/AccountVerificationWarning.vue';
    import { useAuthStore, useCreatorStore, useFeatureFlagsStore } from '../../../../stores';
    import useRoute from '../../../composables/use-route';
    import useRouter from '../../../composables/use-router';

    import useLiveSiteSwitcher from '../../../composables/use-live-site-switcher';
    import { get } from 'lodash';
    import { message } from '@grininc/grin-ui';

    export default {
        name: 'EditProfile',

        components: {
            AccountVerificationWarning,
            RequestRemoval,
            NotificationChannels,
            NotificationSettings,
            EmailVerification,
            AddressInfo,
            ContactInfo,
            LiveSiteSwitcher,
            SettingsBrands,
            AgentInfo
        },

        props: {
            isPage: {
                type: Boolean,
                default: true
            },
            resetDisplayMode: {
                type: Boolean,
                default: false
            },
            changeDisplayMode: {
                type: [Boolean, String],
                default: false
            }
        },

        setup(props, context) {
            const featureFlags = useFeatureFlagsStore().data;
            const router = useRouter().get();
            const { showLiveSiteSwitcher } = useLiveSiteSwitcher();
            const displayMode = ref(SETTINGS_DISPLAY_MODE_DEFAULT);
            const { resetDisplayMode, changeDisplayMode } = toRefs(props);

            const showNotificationSettings = computed(() => {
                return featureFlags['creator-reminders'] || featureFlags['creator-content-metric-alerts'];
            });

            const showAgentInfo = computed(() => {
                return featureFlags['creator-agent-payment'] || false;
            });

            const showSection = (refName) => {
                const element = context.refs[refName];
                const top = element.offsetTop;
                window.scrollTo({
                    top,
                    behavior: 'smooth'
                });
            };

            const handleEmailUpdatedSearchParam = () => {
                const route = useRoute();

                const emailUpdatedQuery = get(route.get().query, 'email_updated');
                if (emailUpdatedQuery) {
                    message.success('Your profile has been updated.');
                    route.removeQueryParam('email_updated');
                }
            };

            const handleResetDisplayMode = () => {
                displayMode.value = SETTINGS_DISPLAY_MODE_DEFAULT;
                context.emit('display-mode-changed', SETTINGS_DISPLAY_MODE_DEFAULT);
            };

            const handleShowBrands = () => {
                displayMode.value = SETTINGS_DISPLAY_MODE_BRANDS;
                context.emit('display-mode-changed', SETTINGS_DISPLAY_MODE_BRANDS);
            };

            const handleBack = () => {
                const previousMode = displayMode.value;
                handleResetDisplayMode();

                if (previousMode === SETTINGS_DISPLAY_MODE_DEFAULT) {
                    router.go(-1);
                }
            };

            onMounted(() => {
                handleEmailUpdatedSearchParam();
                useAuthStore().checkAuth();
                useCreatorStore().getAllLiveUrls();
            });

            watch(resetDisplayMode, () => {
                handleResetDisplayMode();
            });

            watch(changeDisplayMode, () => {
                if (changeDisplayMode.value === SETTINGS_DISPLAY_MODE_DEFAULT) {
                    handleResetDisplayMode();
                } else if (changeDisplayMode.value === SETTINGS_DISPLAY_MODE_BRANDS) {
                    handleShowBrands();
                }
            });

            return {
                SETTINGS_DISPLAY_MODE_BRANDS,
                displayMode,
                featureFlags,
                router,
                useAuthStore,
                useCreatorStore,
                showSection,
                showLiveSiteSwitcher,
                showNotificationSettings,
                handleShowBrands,
                handleBack,
                showAgentInfo
            };
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }
</style>
